import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const BadgeForm = ({ badge, onSave, onCancel, saving, isEditMode }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    category: 'learning',
    points: 10,
    image_url: '',
    is_active: true,
    criteria: {}
  });

  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState('');

  // Initialize form with badge data if editing
  useEffect(() => {
    if (badge) {
      setFormData({
        title: badge?.title || '',
        description: badge?.description || '',
        category: badge?.category || 'learning',
        points: badge?.points || 10,
        image_url: badge?.image_url || '',
        is_active: badge?.is_active !== undefined ? badge?.is_active : true,
        criteria: badge?.criteria || {}
      });
      setImagePreview(badge?.image_url || '');
    }
  }, [badge]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.title?.trim()) {
      newErrors.title = 'Badge title is required';
    }

    if (!formData?.description?.trim()) {
      newErrors.description = 'Badge description is required';
    }

    if (!formData?.category) {
      newErrors.category = 'Category is required';
    }

    if (formData?.points < 1 || formData?.points > 1000) {
      newErrors.points = 'Points must be between 1 and 1000';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }

    // Handle image preview
    if (field === 'image_url') {
      setImagePreview(value);
    }
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      const badgeData = {
        title: formData?.title?.trim(),
        description: formData?.description?.trim(),
        category: formData?.category,
        points: parseInt(formData?.points) || 10,
        image_url: formData?.image_url?.trim() || null,
        is_active: formData?.is_active,
        criteria: formData?.criteria
      };

      onSave(badgeData);
    }
  };

  const getCategoryIcon = (category) => {
    switch (category) {
      case 'learning': return 'BookOpen';
      case 'streak': return 'Zap';
      case 'mastery': return 'Star';
      case 'special': return 'Crown';
      default: return 'Award';
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8">
      {/* Badge Details Section */}
      <div className="space-y-6">
        <div className="flex items-center gap-3 pb-4 border-b border-white border-opacity-20">
          <div className="w-8 h-8 bg-blue-500 bg-opacity-20 rounded-full flex items-center justify-center">
            <Icon name="Award" size={16} className="text-blue-300" />
          </div>
          <h2 className="text-xl font-semibold text-white">Badge Details</h2>
        </div>

        {/* Badge Title */}
        <div>
          <label className="block text-sm font-medium text-blue-100 mb-2">
            Badge Title *
          </label>
          <Input
            value={formData?.title}
            onChange={(e) => handleInputChange('title', e?.target?.value)}
            placeholder="e.g., First Song, Streak Master, Expert Analyst"
            error={errors?.title}
            className="bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200"
          />
        </div>

        {/* Badge Description */}
        <div>
          <label className="block text-sm font-medium text-blue-100 mb-2">
            Description *
          </label>
          <textarea
            value={formData?.description}
            onChange={(e) => handleInputChange('description', e?.target?.value)}
            placeholder="Describe what users need to accomplish to earn this badge..."
            rows={3}
            className={`w-full px-4 py-3 bg-white bg-opacity-10 border border-white border-opacity-30 rounded-lg text-white placeholder-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-transparent resize-none ${
              errors?.description ? 'border-red-400' : ''
            }`}
          />
          {errors?.description && (
            <p className="text-red-400 text-sm mt-1">{errors?.description}</p>
          )}
        </div>

        {/* Category and Points Row */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Category *
            </label>
            <Select
              value={formData?.category}
              onChange={(e) => handleInputChange('category', e?.target?.value)}
              options={[
                { value: 'learning', label: '📚 Learning - Educational milestones' },
                { value: 'streak', label: '⚡ Streak - Consistency achievements' },
                { value: 'mastery', label: '⭐ Mastery - Skill level achievements' },
                { value: 'special', label: '👑 Special - Unique accomplishments' }
              ]}
              error={errors?.category}
              className="bg-white bg-opacity-10 border-white border-opacity-30 text-white"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-blue-100 mb-2">
              Points Value *
            </label>
            <Input
              type="number"
              value={formData?.points}
              onChange={(e) => handleInputChange('points', parseInt(e?.target?.value) || 0)}
              placeholder="10"
              min="1"
              max="1000"
              error={errors?.points}
              className="bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200"
            />
          </div>
        </div>
      </div>
      {/* Visual Assets Section */}
      <div className="space-y-6">
        <div className="flex items-center gap-3 pb-4 border-b border-white border-opacity-20">
          <div className="w-8 h-8 bg-purple-500 bg-opacity-20 rounded-full flex items-center justify-center">
            <Icon name="Image" size={16} className="text-purple-300" />
          </div>
          <h2 className="text-xl font-semibold text-white">Visual Assets</h2>
        </div>

        {/* Badge Icon URL */}
        <div>
          <label className="block text-sm font-medium text-blue-100 mb-2">
            Badge Icon URL
          </label>
          <Input
            type="url"
            value={formData?.image_url}
            onChange={(e) => handleInputChange('image_url', e?.target?.value)}
            placeholder="https://example.com/badge-icon.png"
            className="bg-white bg-opacity-10 border-white border-opacity-30 text-white placeholder-blue-200"
          />
          <p className="text-sm text-blue-200 mt-1">
            Optional: Provide a URL for the badge icon. Supports SVG, PNG, JPG formats.
          </p>
        </div>

        {/* Image Preview */}
        {(imagePreview || formData?.category) && (
          <div className="flex items-center gap-4 p-4 bg-white bg-opacity-5 rounded-lg">
            <div className="text-sm text-blue-100">Preview:</div>
            <div className="flex items-center gap-2">
              {imagePreview ? (
                <img 
                  src={imagePreview} 
                  alt="Badge preview"
                  className="w-12 h-12 rounded-lg object-cover"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
              ) : null}
              <div className={`w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center ${imagePreview ? 'hidden' : 'flex'}`}>
                <Icon 
                  name={getCategoryIcon(formData?.category)} 
                  size={24} 
                  className="text-blue-200" 
                />
              </div>
              <span className="text-white font-medium">{formData?.title || 'Badge Title'}</span>
            </div>
          </div>
        )}
      </div>
      {/* Badge Configuration Section */}
      <div className="space-y-6">
        <div className="flex items-center gap-3 pb-4 border-b border-white border-opacity-20">
          <div className="w-8 h-8 bg-green-500 bg-opacity-20 rounded-full flex items-center justify-center">
            <Icon name="Settings" size={16} className="text-green-300" />
          </div>
          <h2 className="text-xl font-semibold text-white">Badge Configuration</h2>
        </div>

        {/* Badge Status */}
        <div>
          <label className="block text-sm font-medium text-blue-100 mb-3">
            Badge Status
          </label>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => handleInputChange('is_active', true)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                formData?.is_active
                  ? 'bg-green-500 bg-opacity-20 text-green-300 border border-green-500 border-opacity-30' :'bg-white bg-opacity-10 text-blue-200 border border-white border-opacity-20'
              }`}
            >
              <Icon name="Eye" size={16} className="inline mr-1" />
              Active
            </button>
            <button
              type="button"
              onClick={() => handleInputChange('is_active', false)}
              className={`px-4 py-2 rounded-lg font-medium transition-all ${
                !formData?.is_active
                  ? 'bg-red-500 bg-opacity-20 text-red-300 border border-red-500 border-opacity-30' :'bg-white bg-opacity-10 text-blue-200 border border-white border-opacity-20'
              }`}
            >
              <Icon name="EyeOff" size={16} className="inline mr-1" />
              Inactive
            </button>
          </div>
          <p className="text-sm text-blue-200 mt-2">
            Active badges can be earned by users. Inactive badges are hidden from the system.
          </p>
        </div>
      </div>
      {/* Form Actions */}
      <div className="flex items-center justify-end gap-4 pt-6 border-t border-white border-opacity-20">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={saving}
          className="border-white text-white hover:bg-white hover:text-indigo-900 rounded-lg"
        >
          Cancel
        </Button>
        
        <Button
          type="submit"
          variant="default"
          disabled={saving}
          iconName={saving ? "Loader" : "Save"}
          iconPosition="left"
          className="bg-white text-indigo-900 hover:bg-gray-100 rounded-lg"
        >
          {saving ? 'Saving...' : (isEditMode ? 'Update Badge' : 'Create Badge')}
        </Button>
      </div>
    </form>
  );
};

export default BadgeForm;